/**
 * Datora Multi Discounts — the discount badge and discounted price display.
 *
 * snippets/datora-price.liquid renders the data hooks and empty output slots inside every
 * .product--pricing row (PDP, sticky bars, quick view, cards, search, recommendations); this
 * file fills them from co2.itemDiscountFull() and flips the row to the discounted display
 * (see assets/component-datora-badge.css). snippets/datora-line-discount.liquid carries the
 * same hooks on a cart line, badge only — the line prices there are server-rendered.
 *
 * Requires the "Datora | Multi Discounts" app embed (global `co2`).
 */

(function () {
  const PRICE_SELECTOR = '[data-datora-product-id]';
  const ROW_SELECTOR = '.product--pricing';
  const ROOT_SELECTOR = '[data-product-card], product-set';
  const CARD_SELECTOR = '[data-product-card]';
  const CLASS_PENDING = 'is-datora-pending';
  const CLASS_DISCOUNTED = 'is-datora-discounted';
  const CLASS_HIDDEN = 'hidden';
  const PENDING_TIMEOUT = 5000;

  /** Answer promises keyed by every argument the app is given. Cleared on a cart change. */
  let answers = new Map();

  /** Latest pass per element; an older pass discards its answer. */
  const passes = new WeakMap();
  const pendingTimers = new WeakMap();

  function startPass(el) {
    const pass = (passes.get(el) || 0) + 1;
    passes.set(el, pass);
    return pass;
  }

  /** The price row the hook sits in; null on a cart line. */
  function rowFor(el) {
    return el.closest(ROW_SELECTOR);
  }

  /** The card or product info the row belongs to; carries the marker that hides the theme's sale badges. */
  function rootFor(el) {
    return el.closest(ROOT_SELECTOR);
  }

  /** The image badge slot of a card (snippets/badges.liquid). Scoped to the card so a grid does not hand back the neighbour's. */
  function mirrorFor(el) {
    const card = el.closest(CARD_SELECTOR);
    return card ? card.querySelector('[data-datora-image-mirror]') : null;
  }

  function hide(el) {
    if (el) el.classList.add(CLASS_HIDDEN);
  }

  function show(el) {
    if (el) el.classList.remove(CLASS_HIDDEN);
  }

  function shopSettings() {
    return window.settings || {};
  }

  /** The format the row itself uses: cards follow settings.currency_code_enabled, the PDP does not. */
  function moneyFormat(el) {
    const shop = shopSettings();
    if (el && el.dataset.datoraMoney === 'with_currency' && shop.moneyWithCurrencyFormat) {
      return shop.moneyWithCurrencyFormat;
    }
    return shop.moneyFormat || '{{amount}}';
  }

  function formatMoney(cents, format) {
    if (window.Currency && typeof Currency.formatMoney === 'function') return Currency.formatMoney(cents, format);
    return (cents / 100).toFixed(2);
  }

  function priceMarkup(cents, varies, el) {
    const money = formatMoney(cents, moneyFormat(el));
    if (!varies) return money;

    const template = (window.theme && theme.datoraStrings && theme.datoraStrings.fromPriceHtml) || '';
    return template ? template.replace('__PRICE__', money) : money;
  }

  function clearPending(el) {
    const row = rowFor(el);
    if (!row) return;

    const timer = pendingTimers.get(row);
    if (timer) {
      clearTimeout(timer);
      pendingTimers.delete(row);
    }
    row.classList.remove(CLASS_PENDING);
  }

  /**
   * The pending class holds back an undiscounted price, so it must lift itself when the app
   * is missing, blocked, or answers with a promise that never settles. Per row, because
   * blocks keep arriving after page load.
   */
  function schedulePendingFallback(elements) {
    elements.forEach(function (el) {
      const row = rowFor(el);
      if (!row || !row.classList.contains(CLASS_PENDING)) return;
      if (pendingTimers.has(row)) return;

      pendingTimers.set(
        row,
        setTimeout(function () {
          pendingTimers.delete(row);
          row.classList.remove(CLASS_PENDING);
        }, PENDING_TIMEOUT)
      );
    });
  }

  /** Snapshot: after the await, the element and the active code may describe something else. */
  function readRequest(el, code) {
    const productId = Number(el.dataset.datoraProductId);
    const variantId = Number(el.dataset.datoraVariantId);
    const price = Number(el.dataset.datoraPrice);
    if (!productId || !variantId || !price) return null;

    return {
      productId: productId,
      variantId: variantId,
      price: price,
      // The compare-at price of a sale variant; 0 otherwise.
      compareAt: Number(el.dataset.datoraComparePrice) || 0,
      // Rounded by Liquid; read here so the badge cannot differ from the row's own sale badge.
      salePct: el.dataset.datoraSalePct,
      // Cart lines pass their real quantity; a product surface prices one unit. price is per
      // unit either way.
      quantity: Number(el.dataset.datoraQuantity) || 1,
      // Set on a cart line only: the exact reduction, straight from Shopify.
      saved: Number(el.dataset.datoraSaved) || 0,
      // The theme's unit price for this variant, in cents; 0 when there is none.
      unitPrice: Number(el.dataset.datoraUnitPrice) || 0,
      tags: el.dataset.datoraTags ? el.dataset.datoraTags.split(',') : [],
      onSale: el.dataset.datoraSalePrice === 'true',
      varies: el.dataset.datoraPriceVaries === 'true',
      code: code
    };
  }

  /** Back to the theme's own display: the row shows its price again, badge and markers come down. */
  function resetPrice(el) {
    // Invalidates a pass still awaiting its answer; otherwise that answer would land on a
    // hook that has since been reset and hide the theme's price under it.
    startPass(el);
    clearPending(el);

    const row = rowFor(el);
    if (row) row.classList.remove(CLASS_DISCOUNTED);

    const root = rootFor(el);
    if (root) root.classList.remove(CLASS_DISCOUNTED);

    // Emptied, not just hidden: a screen reader that ignores the class would read the amount.
    ['[data-datora-price-out]', '[data-datora-compare-out]', '[data-datora-unit-out]'].forEach(function (selector) {
      const out = el.querySelector(selector);
      if (out) out.innerHTML = '';
    });

    // A cart line's badge is Liquid-rendered from the reduction Shopify actually applied, so
    // an app that has no answer leaves it standing; a product surface has nothing to show
    // without the app. An answer the app does give overwrites it in applyDiscount.
    const badge = el.querySelector('[data-datora-badge]');
    if (badge && !(Number(el.dataset.datoraSaved) > 0)) {
      badge.textContent = '';
      hide(badge);
    }

    const mirror = mirrorFor(el);
    if (mirror) {
      mirror.textContent = '';
      hide(mirror);
    }
  }

  /**
   * The sale share of a sale variant in whole percent, 0 otherwise. Liquid rounds it and hands
   * it over; the fallback keeps Liquid's order of operations ((compare − price) × 100 ÷ compare),
   * or the rounding drifts by a point between surfaces.
   */
  function salePercent(request) {
    if (!(request.compareAt > request.price)) return 0;

    const pct = parseInt(request.salePct, 10);
    if (!isNaN(pct)) return pct;
    return Math.round(((request.compareAt - request.price) * 100) / request.compareAt);
  }

  /**
   * Badge text, or null when the code does nothing to this variant: the code's value, preceded
   * by the sale share where there is one ("-40% + 10%", "-40% + 5,00 €"). Never the code's
   * name. Goes in via textContent.
   */
  function badgeText(data, request, el) {
    let value = '';

    if (data.calc === 'perc' && data.discPerc > 0) {
      value = data.discPerc + '%';
    } else if (data.calc === 'amt' && data.discAmt > 0) {
      // request.saved where Shopify has already worked it out (a cart line, the line total),
      // otherwise the app's own figure for the one unit on show.
      const saved = request.saved || request.price - data.discPrice;
      if (saved <= 0) return null;
      value = formatMoney(saved, moneyFormat(el));
    } else {
      return null;
    }

    // A sale share under one percent rounds to nothing worth naming.
    const salePct = salePercent(request);
    return salePct >= 1 ? '-' + salePct + '% + ' + value : '-' + value;
  }

  function applyDiscount(el, request, data) {
    const text = badgeText(data, request, el);
    const reduced = request.saved > 0 || data.discPrice < request.price;
    if (!text || !reduced) {
      resetPrice(el);
      return;
    }

    // Exactly one struck price. On a sale variant it is the compare-at price and the badge
    // names both reductions; otherwise it is what the shopper pays without the code. Never
    // wrapped in "From": the theme strikes a bare amount beside its own "From" price.
    const compareOut = el.querySelector('[data-datora-compare-out]');
    const struck = request.compareAt > request.price ? request.compareAt : request.price;
    if (compareOut) compareOut.innerHTML = priceMarkup(struck, false, el);

    const priceOut = el.querySelector('[data-datora-price-out]');
    if (priceOut) priceOut.innerHTML = priceMarkup(data.discPrice, request.varies, el);

    // The theme's unit price scaled by the same reduction, so the base price keeps matching
    // the price actually paid. The theme formats unit prices without the currency code.
    const unitOut = el.querySelector('[data-datora-unit-out]');
    if (unitOut) {
      const unit = request.unitPrice > 0 ? Math.round((request.unitPrice * data.discPrice) / request.price) : 0;
      unitOut.innerHTML = unit > 0 ? formatMoney(unit, shopSettings().moneyFormat || '{{amount}}') : '';
    }

    const badge = el.querySelector('[data-datora-badge]');
    if (badge) {
      badge.textContent = text;
      show(badge);
    }

    const mirror = mirrorFor(el);
    if (mirror) {
      mirror.textContent = text;
      show(mirror);
    }

    const root = rootFor(el);
    if (root) root.classList.add(CLASS_DISCOUNTED);

    const row = rowFor(el);
    if (row) row.classList.add(CLASS_DISCOUNTED);

    clearPending(el);
  }

  function askApp(request) {
    const key = [
      request.code,
      request.productId,
      request.variantId,
      request.price,
      request.quantity,
      request.onSale,
      request.tags.join(',')
    ].join('|');

    if (!answers.has(key)) {
      // itemDiscountFull includes conditional discounts (tiered pricing, BOGO), which change
      // the price actually paid.
      const answer = co2
        .itemDiscountFull(
          request.productId,
          request.variantId,
          request.tags,
          request.price,
          request.quantity,
          false,
          request.onSale
        )
        .catch(function (error) {
          console.error('[datora] discount lookup failed:', error);
          return null;
        });
      answers.set(key, answer);
    }

    return answers.get(key);
  }

  /** Sequential: the app answers one variant per call, and the cache absorbs the repeats. */
  async function updateDatoraPrices(scope) {
    scope = scope || document;

    const elements = [];
    if (scope.matches && scope.matches(PRICE_SELECTOR)) elements.push(scope);
    if (scope.querySelectorAll) elements.push.apply(elements, scope.querySelectorAll(PRICE_SELECTOR));
    if (!elements.length) return;

    schedulePendingFallback(elements);

    if (typeof co2 === 'undefined') return;

    // Claimed before the first await, so a call that starts later always outranks this one.
    // Reserving after getDiscountCode() would let a slow lookup take the newer claim and
    // render the previous code's prices.
    const claims = elements.map(function (el) {
      return { el: el, pass: startPass(el) };
    });

    // Local, not shared: two overlapping refreshes around a code change would otherwise
    // resolve into one variable, and whichever finished last would win for both.
    let code = null;
    try {
      code = await co2.getDiscountCode();
    } catch (error) {
      code = null;
    }

    const current = claims.filter(function (claim) {
      return claim.el.isConnected && passes.get(claim.el) === claim.pass;
    });

    if (!code) {
      current.forEach(function (claim) {
        resetPrice(claim.el);
      });
      return;
    }

    for (const claim of current) {
      const el = claim.el;
      if (!el.isConnected || passes.get(el) !== claim.pass) continue;

      const request = readRequest(el, code);
      if (!request) {
        resetPrice(el);
        continue;
      }

      const data = await askApp(request);

      // A newer pass owns the element now — its answer is the current one.
      if (!el.isConnected || passes.get(el) !== claim.pass) continue;

      if (data) {
        applyDiscount(el, request, data);
      } else {
        // On a cart line the app has no answer for, a native Shopify code, this keeps the
        // Liquid-rendered badge; see resetPrice.
        resetPrice(el);
      }
    }
  }

  window.datoraUpdatePrices = updateDatoraPrices;
  window.datoraResetPriceElement = resetPrice;

  // ---------------------------------------------------------------------------
  // Triggers
  // ---------------------------------------------------------------------------

  /**
   * theme.js rewrites a price row's innerHTML on every variant change (Variants._renderProductInfo,
   * _renderCardInfo), the section renderer swaps whole cards and drawers, and quick view,
   * predictive search and recommendations insert new ones. The observer covers all of them, so
   * no call site needs to know Datora exists. childList only: the nodes this file writes sit
   * below the selector, and an attribute observer would see its own class writes.
   *
   * A hook that has just been (re)inserted describes the variant now on show, while its row
   * and card may still carry the previous variant's markers, hiding the theme's price under
   * empty slots. Those come down synchronously, before paint; the lookup re-raises them.
   */
  function dropStaleState(el) {
    const row = rowFor(el);
    if (row) row.classList.remove(CLASS_DISCOUNTED);

    const root = rootFor(el);
    if (root) root.classList.remove(CLASS_DISCOUNTED);
  }

  /**
   * A row swapped to a variant that gets no hook (sold out, volume pricing) keeps the previous
   * variant's discounted class, since theme.js replaces only its contents: the class would hide
   * the theme's price with nothing in its place. Nothing will answer for that row, so its state
   * comes down here, including a pending dim and a mirrored image badge.
   */
  function dropRowState(row) {
    const timer = pendingTimers.get(row);
    if (timer) {
      clearTimeout(timer);
      pendingTimers.delete(row);
    }
    row.classList.remove(CLASS_DISCOUNTED, CLASS_PENDING);

    const root = rootFor(row);
    if (root) root.classList.remove(CLASS_DISCOUNTED);

    const mirror = mirrorFor(row);
    if (mirror) {
      mirror.textContent = '';
      hide(mirror);
    }
  }

  const queued = new Set();
  let flushScheduled = false;

  function flushQueue() {
    flushScheduled = false;
    const nodes = Array.from(queued);
    queued.clear();
    nodes.forEach(function (node) {
      if (node.isConnected) updateDatoraPrices(node);
    });
  }

  new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      const swapped = mutation.target;
      if (swapped.nodeType === Node.ELEMENT_NODE && swapped.matches(ROW_SELECTOR) && !swapped.querySelector(PRICE_SELECTOR)) {
        dropRowState(swapped);
      }

      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const hooks = [];
        if (node.matches(PRICE_SELECTOR)) hooks.push(node);
        hooks.push.apply(hooks, node.querySelectorAll(PRICE_SELECTOR));
        if (!hooks.length) continue;

        hooks.forEach(dropStaleState);
        queued.add(node);
      }
    }

    if (queued.size && !flushScheduled) {
      flushScheduled = true;
      requestAnimationFrame(flushQueue);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /** Answers are per code, so a cart change invalidates all of them. */
  function refreshAll() {
    answers = new Map();
    updateDatoraPrices(document);
  }

  window.addEventListener('co2.cart.initialized', refreshAll);

  function init() {
    updateDatoraPrices(document);
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
